import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShoppingBag, ArrowLeft, Landmark } from "lucide-react";

export default function JerseyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [j, setJ] = useState(null);
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  // Structured shipping address
  const [shipStreet, setShipStreet] = useState("");
  const [shipCity, setShipCity] = useState("");
  const [shipPostal, setShipPostal] = useState("");
  const [shipCountry, setShipCountry] = useState("");
  const [loading, setLoading] = useState(false);
  const [activeImg, setActiveImg] = useState("");
  const [kitVersion, setKitVersion] = useState("fan"); // "fan" | "player"
  const [sleeve, setSleeve] = useState("short"); // "short" | "long"
  const [printName, setPrintName] = useState("");
  const [printNumber, setPrintNumber] = useState("");
  const [patch, setPatch] = useState("none"); // "none" | "league" | "tournament"
  const [addons, setAddons] = useState([]); // fetched list of available add-ons
  const [selectedAddons, setSelectedAddons] = useState([]); // array of add-on ids

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/jerseys/${id}`);
        setJ(r.data);
        if (r.data.sizes?.length) setSize(r.data.sizes[0]);
        setActiveImg(r.data.image_url);
      } catch {
        toast.error("Jersey not found");
      }
    })();
  }, [id]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/addons");
        setAddons(r.data || []);
      } catch {
        // Silent — add-ons are optional
      }
    })();
  }, []);

  const buy = async () => {
    if (!email || !name || !size) {
      toast.error("Please complete name, email and size");
      return;
    }
    if (!shipStreet || !shipCity || !shipPostal || !shipCountry) {
      toast.error("Please complete the full shipping address");
      return;
    }
    if (printNumber && (parseInt(printNumber) < 1 || parseInt(printNumber) > 99)) {
      toast.error("Shirt number must be between 1 and 99");
      return;
    }
    const shipping_address = [shipStreet.trim(), shipCity.trim(), shipPostal.trim(), shipCountry.trim()].join(", ");
    setLoading(true);
    try {
      const res = await api.post("/orders", {
        item_type: "jersey",
        item_id: id,
        quantity: qty,
        size,
        customer_email: email,
        customer_name: name,
        shipping_address,
        kit_version: kitVersion,
        sleeve,
        print_name: printName.trim().toUpperCase().slice(0, 12) || null,
        print_number: printNumber ? parseInt(printNumber) : null,
        patch: patch === "none" ? null : patch,
        add_on_ids: selectedAddons,
      });
      nav(`/order/${res.data.order_reference}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setLoading(false);
    }
  };

  if (!j) return <div className="max-w-7xl mx-auto px-6 py-20 font-mono-retro text-[#A8A39D] uppercase tracking-[0.2em]">Loading the kit…</div>;

  const basePrice = Number(j.price);
  const unitPrice = basePrice;
  const addonsSubtotal = addons
    .filter((a) => selectedAddons.includes(a.id))
    .reduce((s, a) => s + Number(a.price), 0);
  const total = unitPrice * qty + addonsSubtotal * qty;
  const gallery = [j.image_url, ...(j.additional_images || [])].filter(Boolean);

  return (
    <div data-testid="jersey-detail" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <button onClick={() => nav(-1)} data-testid="back-btn" className="font-mono-retro text-xs tracking-[0.3em] text-[#A8A39D] uppercase inline-flex items-center gap-2 hover:text-[#C8A96A] mb-8">
        <ArrowLeft size={14} /> Back to Shop
      </button>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="border border-[#332D2B] bg-[#2A2320] aspect-square overflow-hidden">
            <img src={activeImg} alt={j.name} className="w-full h-full object-cover" />
          </div>
          {gallery.length > 1 && (
            <div data-testid="jersey-thumbs" className="grid grid-cols-5 gap-2 mt-3">
              {gallery.map((src, i) => (
                <button
                  key={i}
                  data-testid={`thumb-${i}`}
                  onClick={() => setActiveImg(src)}
                  className={`aspect-square border bg-[#2A2320] overflow-hidden transition-colors ${activeImg === src ? "border-[#C8A96A]" : "border-[#332D2B] hover:border-[#A8A39D]"}`}
                >
                  <img src={src} alt={`view ${i + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-5">
          <div className="font-mono-retro text-[10px] tracking-[0.3em] text-[#756F6B] uppercase">{j.team} · {j.season}</div>
          <h1 className="font-display text-5xl text-[#F2EBDD] mt-2 leading-tight">{j.name}</h1>
          <div className="text-[#A8A39D] mt-1">{j.player}</div>
          <div className="font-display text-4xl text-[#C8A96A] mt-5">£{basePrice.toFixed(2)}</div>
          <p className="text-[#F2EBDD]/90 mt-4 leading-relaxed">{j.description}</p>

          <div className="border border-[#332D2B] bg-[#1C1816] p-6 mt-7">
            {/* Kit Version */}
            <label className="fz-label">Kit Version</label>
            <div className="grid grid-cols-2 gap-2" data-testid="kit-version-group">
              <button
                type="button"
                data-testid="kit-version-fan"
                onClick={() => setKitVersion("fan")}
                className={`p-3 border text-left transition-colors ${
                  kitVersion === "fan" ? "border-[#C8A96A] bg-[#0F0C0B]" : "border-[#332D2B] hover:border-[#A8A39D]"
                }`}
              >
                <div className="font-mono-retro text-[10px] tracking-[0.25em] text-[#A8A39D] uppercase">Fan</div>
                <div className="font-display text-xl text-[#F2EBDD] mt-1">Replica</div>
                <div className="text-[11px] text-[#756F6B] mt-1">Standard fit · everyday wear</div>
              </button>
              <button
                type="button"
                data-testid="kit-version-player"
                onClick={() => setKitVersion("player")}
                className={`p-3 border text-left transition-colors ${
                  kitVersion === "player" ? "border-[#C8A96A] bg-[#0F0C0B]" : "border-[#332D2B] hover:border-[#A8A39D]"
                }`}
              >
                <div className="font-mono-retro text-[10px] tracking-[0.25em] text-[#C8A96A] uppercase">Player</div>
                <div className="font-display text-xl text-[#F2EBDD] mt-1">Match</div>
                <div className="text-[11px] text-[#756F6B] mt-1">Authentic cut · pro fabric</div>
              </button>
            </div>

            {/* Sleeve Length (only when jersey offers long sleeve) */}
            {j.has_long_sleeve && (
              <>
                <label className="fz-label mt-5">Sleeve Length</label>
                <div className="grid grid-cols-2 gap-2" data-testid="sleeve-group">
                  <button
                    type="button"
                    data-testid="sleeve-short"
                    onClick={() => setSleeve("short")}
                    className={`p-3 border text-left transition-colors ${
                      sleeve === "short" ? "border-[#C8A96A] bg-[#0F0C0B]" : "border-[#332D2B] hover:border-[#A8A39D]"
                    }`}
                  >
                    <div className={`font-display text-lg ${sleeve === "short" ? "text-[#C8A96A]" : "text-[#F2EBDD]"}`}>Short</div>
                    <div className="font-mono-retro text-[9px] tracking-[0.2em] text-[#756F6B] uppercase mt-1">Classic cut</div>
                  </button>
                  <button
                    type="button"
                    data-testid="sleeve-long"
                    onClick={() => setSleeve("long")}
                    className={`p-3 border text-left transition-colors ${
                      sleeve === "long" ? "border-[#C8A96A] bg-[#0F0C0B]" : "border-[#332D2B] hover:border-[#A8A39D]"
                    }`}
                  >
                    <div className={`font-display text-lg ${sleeve === "long" ? "text-[#C8A96A]" : "text-[#F2EBDD]"}`}>Long</div>
                    <div className="font-mono-retro text-[9px] tracking-[0.2em] text-[#756F6B] uppercase mt-1">Winter kit</div>
                  </button>
                </div>
              </>
            )}

            {/* Custom Printing */}
            <div className="mt-5 border border-[#332D2B] bg-[#0F0C0B] p-4">
              <label className="fz-label mb-0">Custom Printing</label>
              <div className="text-[11px] text-[#756F6B] mb-3">Optional — free name &amp; number on the back</div>
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <label className="fz-label text-[10px]">Name on back</label>
                  <input
                    data-testid="print-name"
                    className="fz-input uppercase"
                    maxLength={12}
                    placeholder="e.g. MESSI"
                    value={printName}
                    onChange={(e) => setPrintName(e.target.value.replace(/[^A-Za-z .'-]/g, ""))}
                  />
                </div>
                <div>
                  <label className="fz-label text-[10px]">Number</label>
                  <input
                    data-testid="print-number"
                    type="number"
                    min={1}
                    max={99}
                    className="fz-input"
                    placeholder="10"
                    value={printNumber}
                    onChange={(e) => setPrintNumber(e.target.value.replace(/[^0-9]/g, "").slice(0, 2))}
                  />
                </div>
              </div>
            </div>

            {/* Sleeve Patch */}
            <div className="mt-5 border border-[#332D2B] bg-[#0F0C0B] p-4">
              <label className="fz-label mb-0">Sleeve Patch</label>
              <div className="text-[11px] text-[#756F6B] mb-3">Optional — choose the patch for your kit&apos;s sleeve</div>
              <div className="grid grid-cols-3 gap-2" data-testid="patch-group">
                {[
                  { value: "none", label: "No Patch", sub: "Clean sleeve" },
                  { value: "league", label: "League", sub: "Domestic league" },
                  { value: "tournament", label: "Tournament", sub: "UCL / World Cup" },
                ].map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    data-testid={`patch-${p.value}`}
                    onClick={() => setPatch(p.value)}
                    className={`p-3 border text-left transition-colors ${
                      patch === p.value ? "border-[#C8A96A] bg-[#1C1816]" : "border-[#332D2B] hover:border-[#A8A39D]"
                    }`}
                  >
                    <div className={`font-display text-base leading-tight ${patch === p.value ? "text-[#C8A96A]" : "text-[#F2EBDD]"}`}>{p.label}</div>
                    <div className="font-mono-retro text-[9px] tracking-[0.2em] text-[#756F6B] uppercase mt-1">{p.sub}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Extras / Add-ons */}
            {addons.length > 0 && (
              <div className="mt-5 border border-[#332D2B] bg-[#0F0C0B] p-4" data-testid="addons-block">
                <label className="fz-label mb-0">Extras</label>
                <div className="text-[11px] text-[#756F6B] mb-3">Complete the boardroom — add a matchball or accessory</div>
                <div className="space-y-2">
                  {addons.map((a) => {
                    const checked = selectedAddons.includes(a.id);
                    return (
                      <button
                        key={a.id}
                        type="button"
                        data-testid={`addon-${a.id}`}
                        onClick={() => setSelectedAddons((cur) => checked ? cur.filter((x) => x !== a.id) : [...cur, a.id])}
                        className={`w-full flex items-center gap-3 p-3 border text-left transition-colors ${
                          checked ? "border-[#C8A96A] bg-[#1C1816]" : "border-[#332D2B] hover:border-[#A8A39D]"
                        }`}
                      >
                        {a.image_url ? (
                          <img src={a.image_url} alt="" className="w-14 h-14 object-cover border border-[#332D2B] shrink-0" />
                        ) : (
                          <div className="w-14 h-14 border border-dashed border-[#332D2B] shrink-0 flex items-center justify-center font-display text-xl text-[#756F6B]">⚽</div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className={`font-display text-base leading-tight ${checked ? "text-[#C8A96A]" : "text-[#F2EBDD]"}`}>{a.name}</div>
                          <div className="font-mono-retro text-[9px] tracking-[0.2em] text-[#756F6B] uppercase mt-1 line-clamp-1">{a.description}</div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className={`font-display text-lg ${checked ? "text-[#C8A96A]" : "text-[#F2EBDD]"}`}>+£{Number(a.price).toFixed(2)}</div>
                          <div className="font-mono-retro text-[9px] tracking-[0.2em] uppercase mt-1" style={{ color: checked ? "#7FB77E" : "#756F6B" }}>
                            {checked ? "✓ Added" : "Add"}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <label className="fz-label mt-5">Size</label>
            <div className="flex flex-wrap gap-2">
              {(j.sizes || []).map((s) => (
                <button
                  key={s}
                  data-testid={`size-${s}`}
                  onClick={() => setSize(s)}
                  className={`px-4 py-2 border font-mono-retro text-xs tracking-[0.2em] uppercase transition-colors ${
                    size === s ? "border-[#C8A96A] text-[#C8A96A] bg-[#0F0C0B]" : "border-[#332D2B] text-[#A8A39D] hover:border-[#C8A96A]"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            <label className="fz-label mt-5">Quantity</label>
            <input
              data-testid="jersey-qty"
              type="number"
              min={1}
              max={j.stock}
              value={qty}
              onChange={(e) => setQty(Math.max(1, Math.min(j.stock, parseInt(e.target.value) || 1)))}
              className="fz-input"
            />

            <div className="grid grid-cols-2 gap-3 mt-5">
              <div>
                <label className="fz-label">Full Name</label>
                <input data-testid="jersey-name" className="fz-input" value={name} onChange={(e) => setName(e.target.value)} />
              </div>
              <div>
                <label className="fz-label">Email</label>
                <input data-testid="jersey-email" type="email" className="fz-input" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
            </div>

            <label className="fz-label mt-4">Shipping Address</label>
            <div className="space-y-3">
              <input
                data-testid="ship-street"
                className="fz-input"
                placeholder="House Number and Street"
                value={shipStreet}
                onChange={(e) => setShipStreet(e.target.value)}
                autoComplete="address-line1"
              />
              <div className="grid grid-cols-2 gap-3">
                <input
                  data-testid="ship-city"
                  className="fz-input"
                  placeholder="City"
                  value={shipCity}
                  onChange={(e) => setShipCity(e.target.value)}
                  autoComplete="address-level2"
                />
                <input
                  data-testid="ship-postal"
                  className="fz-input"
                  placeholder="Postal Code"
                  value={shipPostal}
                  onChange={(e) => setShipPostal(e.target.value)}
                  autoComplete="postal-code"
                />
              </div>
              <input
                data-testid="ship-country"
                className="fz-input"
                placeholder="Country"
                value={shipCountry}
                onChange={(e) => setShipCountry(e.target.value)}
                autoComplete="country-name"
              />
            </div>

            {/* Shipping note */}
            <div data-testid="shipping-note" className="mt-3 border border-[#332D2B] bg-[#1C1816]/60 px-4 py-3 flex items-start gap-3">
              <span className="font-mono-retro text-[10px] tracking-[0.3em] text-[#C8A96A] uppercase mt-0.5">Note</span>
              <p className="text-[12px] leading-relaxed text-[#A8A39D]">
                Estimated delivery: <span className="text-[#F2EBDD] font-semibold">10–17 days</span> from order date.
                We&apos;ll email you a tracking link as soon as your kit leaves the warehouse.
              </p>
            </div>

            <div className="perforated my-6" />
            {(qty > 1 || addonsSubtotal > 0) && (
              <div className="space-y-1 font-mono-retro text-[11px] tracking-[0.15em] uppercase text-[#A8A39D] mb-2">
                <div className="flex justify-between">
                  <span>Shirt · £{basePrice.toFixed(2)} × {qty}</span>
                  <span>£{(basePrice * qty).toFixed(2)}</span>
                </div>
                {addonsSubtotal > 0 && (
                  <div className="flex justify-between text-[#C8A96A]">
                    <span>Extras × {qty}</span>
                    <span>£{(addonsSubtotal * qty).toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}
            <div className="flex items-center justify-between mb-5">
              <span className="font-mono-retro text-[10px] tracking-[0.3em] text-[#A8A39D] uppercase">Total</span>
              <span data-testid="jersey-total" className="font-display text-3xl text-[#C8A96A]">£{total.toFixed(2)}</span>
            </div>
            <button
              data-testid="jersey-buy-btn"
              onClick={buy}
              disabled={loading || j.stock <= 0}
              className="btn-primary w-full justify-center"
            >
              <ShoppingBag size={16} /> {loading ? "Placing order…" : j.stock <= 0 ? "Sold Out" : "Continue to Bank Transfer"}
            </button>
            <div className="font-mono-retro text-[10px] tracking-[0.25em] text-[#756F6B] uppercase text-center mt-3 inline-flex items-center gap-2 w-full justify-center">
              <Landmark size={12} className="text-[#C8A96A]" /> UK Bank Transfer · Worldwide Shipping
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
